/**
 * Shapes the one-item `sitewideAlert` response from
 * `../getContentfulSitewideAlert.ts` into the alert that
 * `src/components/Shared/AlertBar/AlertBar.tsx` renders. Runs in the browser.
 * Pinned by `./index.spec.ts`.
 */
import {
	ContentfulAPIResponse,
	ContentfulIncludedEntry,
	ContentfulLocationAPIResponseLocationFields,
	getIncludedEntryOrAsset,
} from "@ryerson/frontend.contentful";
import { AxiosResponse } from "axios";
import {
	ContentfulSitewideAlertAPIResponseFields,
	ProcessedContentfulSitewideAlertFields,
} from "../types";

/**
 * Builds the alert from the response's first item: all of its fields, plus
 * `contentful_id` and `locale`, with the optional fields set to `null` when
 * absent.
 *
 * `location` becomes the linked location entry's `fields`, resolved one level
 * from the response's `includes`; `AlertBar` reads only its `sapPlantNumber`.
 * When the link's target isn't in `includes` (for example an unpublished
 * location), `location` is `undefined`, and `AlertBar` then shows the alert to
 * every visitor, as if it had no location.
 *
 * Precondition: the response has at least one item; the caller checks.
 *
 * @param apiResponse The API response from Contentful
 * @returns The processed sitewide alert with fields
 * @throws `TypeError` when the alert links a location and the response has no
 *   `includes` at all.
 */
export const processContentfulSitewideAlertResponse = (
	apiResponse: AxiosResponse<ContentfulAPIResponse<ContentfulSitewideAlertAPIResponseFields>>
): ProcessedContentfulSitewideAlertFields => {
	const includes = apiResponse.data.includes as unknown as {
		Entry: Array<ContentfulIncludedEntry<ContentfulLocationAPIResponseLocationFields>>;
	}; // casting because we know the includes object will be defined for this content type

	const alertEntry = apiResponse.data.items[0];

	const {
		displayOnAllOtherPages,
		linkLabel,
		link,
		location: locationSysLink,
	} = alertEntry.fields;

	const processed = {
		...alertEntry.fields,
		contentful_id: alertEntry.sys.id,
		displayOnAllOtherPages: displayOnAllOtherPages ?? null,
		linkLabel: linkLabel ?? null,
		link: link ?? null,
		location: locationSysLink
			? (getIncludedEntryOrAsset(
					// the location included entry has fields that are also sys links, but resolving those is out of scope for this function
					locationSysLink,
					includes
			  ) as ProcessedContentfulSitewideAlertFields["location"])
			: null,
		locale: alertEntry.sys.locale,
	};

	return processed;
};
